import { PropsWithChildren } from 'react';
import useAsync from 'react-use/esm/useAsync';
import stripAnsi from 'strip-ansi';
import { CompoundEntityRef } from '@backstage/catalog-model';
import {
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Content,
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import { techdocsTranslationRef } from '@backstage/plugin-techdocs/alpha';
import {
  TechDocsReaderPageProvider,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import { Typography } from '@material-ui/core';
import { TechDocsGenerationStatus } from './TechDocsGenerationStatus';
import { fetchBuildStatus } from '../../api/build-status';

function BuildStatusMessage({ builderNote }: { builderNote: string }) {
  const { entityRef, metadata } = useTechDocsReaderPage();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const { namespace, kind, name } = entityRef;
  const { value, loading, error } = useAsync(
    () =>
      fetchBuildStatus({ namespace, kind, name }, { discoveryApi, fetchApi }),
    [namespace, kind, name, discoveryApi, fetchApi],
  );

  if (loading) {
    return <Progress />;
  }
  const buildError = stripAnsi(value?.error ?? '');
  if (value?.status === 'failed') {
    const timestamp = new Date(value.timestamp);
    return (
      <>
        <Typography paragraph>
          The last docs build failed
          {value.commit && (
            <>
              {' on '}
              <code>{value.commit.slice(0, 7)}</code>
            </>
          )}
          {Number.isFinite(timestamp.getTime()) && value.timestamp > 0 && (
            <>
              {' at '}
              <time dateTime={timestamp.toISOString()}>
                {timestamp.toLocaleString(undefined, { timeZoneName: 'short' })}
              </time>
            </>
          )}
        </Typography>
        <Typography component="pre">{buildError}</Typography>
      </>
    );
  }
  if (value?.status === 'skipped') {
    return (
      <Typography paragraph>
        {buildError ||
          'No README.md or docs/ folder was found in the default branch'}
      </Typography>
    );
  }
  return (
    <>
      <Typography paragraph>{builderNote}</Typography>
      {error && <ResponseErrorPanel error={error} />}
      <details>
        <summary>Error details</summary>
        <Typography component="pre">{String(metadata.error)}</Typography>
      </details>
    </>
  );
}

function AvailableDocs({ children }: PropsWithChildren<{}>) {
  const { metadata, entityMetadata } = useTechDocsReaderPage();
  const { t } = useTranslationRef(techdocsTranslationRef);

  if (metadata.loading || entityMetadata.loading) {
    return <Progress />;
  }

  // Only a missing published site gets the onboarding view. Let the reader
  // handle missing entities or annotations, delegated sites, and other errors.
  const error = metadata.error;
  const response = error && 'response' in error ? error.response : undefined;
  const missingSite =
    response !== null &&
    typeof response === 'object' &&
    'status' in response &&
    response.status === 404;
  if (
    !missingSite ||
    !entityMetadata.value ||
    !entityMetadata.value.metadata.annotations?.['backstage.io/techdocs-ref'] ||
    entityMetadata.value.metadata.annotations?.['backstage.io/techdocs-entity']
  ) {
    return <>{children}</>;
  }

  return (
    <Content>
      <InfoCard
        title={t('notFound.title')}
        titleTypographyProps={{ component: 'h2' }}
      >
        <TechDocsGenerationStatus />
        <BuildStatusMessage builderNote={t('notFound.builderNote')} />
      </InfoCard>
    </Content>
  );
}

export function TechDocsAvailability({
  entityRef,
  children,
}: PropsWithChildren<{ entityRef: CompoundEntityRef }>) {
  const config = useApi(configApiRef);
  if (config.getOptionalString('techdocs.builder') !== 'external') {
    return <>{children}</>;
  }

  // TechDocs does not expose its not-found component. Preflight metadata here
  // so the stock reader can retain its layout, routing, and add-ons unchanged.
  return (
    <TechDocsReaderPageProvider entityRef={entityRef}>
      <AvailableDocs>{children}</AvailableDocs>
    </TechDocsReaderPageProvider>
  );
}
